import React from 'react';
import {
  Controller,
  type FieldValues,
  type UseControllerProps,
} from 'react-hook-form';
import Checkbox, { type CheckboxProps } from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import classnames from 'classnames';

type ControlledCheckboxProps<T extends FieldValues> = CheckboxProps &
  UseControllerProps<T> & {
    checkboxLabelClassName?: string;
    error?: boolean;
    formControlClassName?: string;
    formGroupClassName?: string;
    label: React.ReactNode;
  };

export default function ControlledCheckbox<T extends FieldValues>({
  checkboxLabelClassName = undefined,
  control,
  formControlClassName = undefined,
  formGroupClassName = undefined,
  error = false,
  label,
  name,
  required = false,
  rules = {},
  ...props
}: ControlledCheckboxProps<T>) {
  return (
    <FormControl
      className={classnames(formControlClassName)}
      component="fieldset"
    >
      <FormGroup className={classnames('', formGroupClassName)}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              className={classnames(checkboxLabelClassName)}
              control={
                <Checkbox
                  className={classnames(checkboxLabelClassName)}
                  onChange={() => {
                    onChange(!value);
                  }}
                  checked={value}
                  color={error ? 'error' : 'default'}
                  {...props}
                />
              }
              key={value}
              label={label}
              // TODO(jon): The required prop is not available with our version
              // of MUI. We need to upgrade to <= v5.12.1. But I'm leaving it
              // here for forward compatibility.
              // https://github.com/mui/material-ui/releases/tag/v5.12.1
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              required={required}
            />
          )}
          rules={rules}
        />
      </FormGroup>
    </FormControl>
  );
}
